<template>
  <b-row
    v-if="searchEnabled && !isEmpty(address)"
    id="addressResult"
    no-gutters
    class="address-container flex-nowrap p-3"
  >
    <b-col cols="auto">
      <MiniMap :location="location" />
    </b-col>

    <b-col
      cols="auto"
      class="pl-2 flex-shrink-1 flex-grow-1"
    >
      <h5>{{ address }}</h5>
      <p
        v-for="(p, index) in information"
        :key="index"
        :class="{'mb-0': index < information.length -1 }"
      >
        {{ p }}
      </p>
    </b-col>

    <b-button
      class="reset-button mb-3 mr-3"
      squared
      variant="outline-dark"
      @click="resetFilterByLocation"
    >
      Reset
    </b-button>
  </b-row>
</template>

<script lang="ts">

import Vue from 'vue'
import Component from 'vue-class-component'

import MiniMap from '@/components/public/MiniMap.vue'
import { SEARCH_CONFIG_KEY } from '@/constants'

const components = {
  MiniMap,
}

@Component({ components })
export default class AddressResult extends Vue {
  mounted () {
    this.$nextTick(() => {
      this.$forceUpdate()
    })
  }

  get currentLocation () {
    return this.$typedStore.public.search.currentLocation
  }

  get address () {
    return (this.currentLocation && 'display_name' in this.currentLocation && this.currentLocation.display_name) || ''
  }

  get searchEnabled () {
    const config = this.$typedStore.activeVisualisation.visualisationStore.app.config
    if (SEARCH_CONFIG_KEY in config) {
      return config[SEARCH_CONFIG_KEY].enableSearchByLocation
    } else {
      return false
    }
  }

  get location () {
    const { lat, lon } = this.currentLocation
    return [lat, lon]
  }

  get placeholder () {
    return this.currentLocation && 'placeholder' in this.currentLocation && this.currentLocation.placeholder
  }

  get recordsToShow () {
    return this.$typedStore.public.search.recordsToShow
  }

  get allRecords () {
    return this.$typedStore.visualisationData.leafDataForRoot.map(record => record.recordId)
  }

  get lengthOfFilteredRecords () {
    return this.recordsToShow.size
  }

  get lengthOfAllRecords () {
    return this.allRecords.length
  }

  get lengthOfHiddenRecords () {
    return this.lengthOfAllRecords - this.lengthOfFilteredRecords
  }

  get information () {
    if (this.placeholder) {
      return ['The projects have been filtered by location.',
        `${this.lengthOfFilteredRecords} out of ${this.lengthOfAllRecords} projects relate to this address. (For preview purpose only)`]
    } else if (this.lengthOfFilteredRecords > 0) {
      return ['The projects have been filtered by location.',
        `${this.lengthOfFilteredRecords} out of ${this.lengthOfAllRecords} projects relate to this address.`]
    } else {
      return ['No projects match your current filter. Please try again.']
    }
  }

  resetFilterByLocation () {
    this.$typedStore.public.search.resetFilterByLocation()
  }

  isEmpty (value: string) {
    return this.$typedStore.activeVisualisation.visualisationStore.app.isEmpty(value)
  }
}

</script>

<style scoped>

.address-container {
  position: relative;
}

.reset-button {
  position: absolute;
  right: 0;
  bottom: 0;
}

</style>
